import { useEffect, useMemo, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, IconButton, MenuItem, Stack, TextField, ToggleButton,
  ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CodeIcon from '@mui/icons-material/Code';
import ViewListIcon from '@mui/icons-material/ViewList';
import { compileFilter, FilterCompileError } from './FilterExpression';
import type { FilterCombinator, FilterConditionModel, FilterMode } from './types';

// Re-exported under the dialog's historical names so existing importers keep
// working; the canonical definitions live in ../types (they are persisted).
export type { FilterMode } from './types';
export type Combinator = FilterCombinator;
export type FilterCondition = FilterConditionModel;

export interface FilterModel {
  /** Name the user gives this filter so it can be saved and toggled. */
  name: string;
  mode: FilterMode;
  conditions: FilterConditionModel[];
  combinator: FilterCombinator;
  expression: string;
}

interface OperatorDef {
  value: string;
  label: string;
  kind: 'binary' | 'between' | 'list';
  numericOnly?: boolean;
}

const OPERATORS: OperatorDef[] = [
  { value: '==', label: 'equals', kind: 'binary' },
  { value: '!=', label: 'does not equal', kind: 'binary' },
  { value: 'contains', label: 'contains', kind: 'binary' },
  { value: '>', label: 'greater than', kind: 'binary', numericOnly: true },
  { value: '>=', label: 'greater than or equal', kind: 'binary', numericOnly: true },
  { value: '<', label: 'less than', kind: 'binary', numericOnly: true },
  { value: '<=', label: 'less than or equal', kind: 'binary', numericOnly: true },
  { value: 'between', label: 'is between', kind: 'between', numericOnly: true },
  { value: 'in', label: 'is one of', kind: 'list' },
];

let counter = 0;
function nextId(): string {
  counter += 1;
  return `fc_${counter}`;
}

function operatorsFor(isNumeric: boolean): OperatorDef[] {
  return OPERATORS.filter((o) => !o.numericOnly || isNumeric);
}

function kindOf(op: string): OperatorDef['kind'] {
  return OPERATORS.find((o) => o.value === op)?.kind ?? 'binary';
}

/** Format a raw user value into an expression token (quote text, leave numbers raw). */
function formatValue(raw: string, isNumeric: boolean): string | null {
  const t = (raw ?? '').trim();
  if (t === '') return null;
  if (isNumeric) return t;
  return `'${t.replace(/\\/g, '\\\\').replace(/'/g, "\\'")}'`;
}

function buildClause(c: FilterCondition, isNumeric: boolean): string | null {
  if (!c.column || !c.op) return null;
  if (kindOf(c.op) === 'between') {
    const lo = formatValue(c.value, isNumeric);
    const hi = formatValue(c.value2, isNumeric);
    if (lo == null || hi == null) return null;
    return `${c.column} between ${lo} and ${hi}`;
  }
  if (kindOf(c.op) === 'list') {
    const items = (c.value || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .map((s) => formatValue(s, isNumeric))
      .filter((s): s is string => Boolean(s));
    if (!items.length) return null;
    return `${c.column} in (${items.join(', ')})`;
  }
  if (c.op === 'contains') {
    // `contains` always takes a quoted string literal, even on numeric columns.
    const t = (c.value ?? '').trim();
    if (t === '') return null;
    const lit = `'${t.replace(/\\/g, '\\\\').replace(/'/g, "\\'")}'`;
    return `${c.column} contains ${lit}`;
  }
  const v = formatValue(c.value, isNumeric);
  if (v == null) return null;
  return `${c.column} ${c.op} ${v}`;
}

export function buildExpression(
  conditions: FilterCondition[],
  combinator: Combinator,
  numericColumns: Set<string>,
): string {
  const clauses = conditions
    .map((c) => buildClause(c, numericColumns.has(c.column)))
    .filter((c): c is string => Boolean(c));
  if (!clauses.length) return '';
  const join = combinator === 'or' ? ' or ' : ' and ';
  if (clauses.length === 1) return clauses[0];
  return clauses.map((c) => `(${c})`).join(join);
}

interface Props {
  open: boolean;
  columns: string[];
  numericColumns: Set<string>;
  /** distinct sample values per column, for value autocomplete */
  distinctValues: Map<string, string[]>;
  initial: FilterModel;
  /** Other saved filter names — used to reject a duplicate name. */
  existingNames?: string[];
  /** True when editing an existing saved filter (affects the title/CTA). */
  isEdit?: boolean;
  onClose: () => void;
  onApply: (model: FilterModel) => void;
}

const EMPTY_VALUES: string[] = [];

export default function FilterBuilderDialog({
  open, columns, numericColumns, distinctValues, initial, existingNames = [],
  isEdit = false, onClose, onApply,
}: Props) {
  const [name, setName] = useState<string>(initial.name);
  const [mode, setMode] = useState<FilterMode>(initial.mode);
  const [conditions, setConditions] = useState<FilterCondition[]>(initial.conditions);
  const [combinator, setCombinator] = useState<Combinator>(initial.combinator);
  const [expression, setExpression] = useState<string>(initial.expression);

  // Re-seed working state each time the dialog opens.
  useEffect(() => {
    if (!open) return;
    setName(initial.name);
    setMode(initial.mode);
    setConditions(initial.conditions.length
      ? initial.conditions
      : [{ id: nextId(), column: columns[0] ?? '', op: '==', value: '', value2: '' }]);
    setCombinator(initial.combinator);
    setExpression(initial.expression);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const nameError = useMemo(() => {
    const t = name.trim();
    if (!t) return 'Give this filter a name.';
    const clash = existingNames.some((n) => n.trim().toLowerCase() === t.toLowerCase());
    return clash ? 'A filter with this name already exists.' : null;
  }, [name, existingNames]);

  const generated = useMemo(
    () => buildExpression(conditions, combinator, numericColumns),
    [conditions, combinator, numericColumns],
  );

  const exprToValidate = mode === 'builder' ? generated : expression;

  const validation = useMemo(() => {
    const t = exprToValidate.trim();
    if (!t) return { ok: true as const, warning: undefined as string | undefined };
    try {
      const c = compileFilter(t, columns);
      return { ok: true as const, warning: c.warning };
    } catch (e) {
      const msg = e instanceof FilterCompileError ? e.message : (e as Error).message;
      return { ok: false as const, error: msg };
    }
  }, [exprToValidate, columns]);

  const updateCondition = (id: string, patch: Partial<FilterCondition>) => {
    setConditions((cs) => cs.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  };

  const changeColumn = (id: string, column: string) => {
    setConditions((cs) => cs.map((c) => {
      if (c.id !== id) return c;
      const allowed = operatorsFor(numericColumns.has(column)).map((o) => o.value);
      const op = allowed.includes(c.op) ? c.op : '==';
      return { ...c, column, op };
    }));
  };

  const addCondition = () => {
    setConditions((cs) => [
      ...cs,
      { id: nextId(), column: columns[0] ?? '', op: '==', value: '', value2: '' },
    ]);
  };

  const removeCondition = (id: string) => {
    setConditions((cs) => cs.filter((c) => c.id !== id));
  };

  const clearAll = () => {
    setConditions([{ id: nextId(), column: columns[0] ?? '', op: '==', value: '', value2: '' }]);
    setExpression('');
  };

  const handleApply = () => {
    if (!validation.ok || nameError) return;
    onApply({
      name: name.trim(),
      mode,
      conditions,
      combinator,
      expression: exprToValidate.trim(),
    });
  };

  const editAsExpression = () => {
    setExpression(generated);
    setMode('expression');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <span>{isEdit ? 'Edit filter' : 'New filter'}</span>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={mode}
            onChange={(_, v) => v && setMode(v)}
          >
            <ToggleButton value="builder">
              <ViewListIcon fontSize="small" sx={{ mr: 0.5 }} /> Builder
            </ToggleButton>
            <ToggleButton value="expression">
              <CodeIcon fontSize="small" sx={{ mr: 0.5 }} /> Expression
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <TextField
          label="Filter name"
          size="small"
          fullWidth
          autoFocus={!isEdit}
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={!!nameError}
          helperText={nameError ?? 'Stored with the process; toggle it on/off from the filter list.'}
          placeholder="e.g. High-value east region"
          sx={{ mb: 2 }}
        />
        {mode === 'builder' ? (
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Match
              </Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={combinator}
                onChange={(_, v) => v && setCombinator(v)}
              >
                <ToggleButton value="and">ALL conditions</ToggleButton>
                <ToggleButton value="or">ANY condition</ToggleButton>
              </ToggleButtonGroup>
            </Stack>

            <Divider />

            <Stack spacing={1}>
              {conditions.map((c, idx) => {
                const isNum = numericColumns.has(c.column);
                const ops = operatorsFor(isNum);
                const kind = kindOf(c.op);
                const opts = distinctValues.get(c.column) ?? EMPTY_VALUES;
                return (
                  <Stack
                    key={c.id}
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ flexWrap: { xs: 'wrap', sm: 'nowrap' } }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ width: 36, color: 'text.secondary', flexShrink: 0 }}
                    >
                      {idx === 0 ? 'where' : (combinator === 'or' ? 'or' : 'and')}
                    </Typography>

                    <TextField
                      select size="small" label="Column"
                      value={c.column}
                      onChange={(e) => changeColumn(c.id, e.target.value)}
                      sx={{ minWidth: 160, flex: 1 }}
                    >
                      {columns.map((col) => (
                        <MenuItem key={col} value={col}>{col}</MenuItem>
                      ))}
                    </TextField>

                    <TextField
                      select size="small" label="Condition"
                      value={c.op}
                      onChange={(e) => updateCondition(c.id, { op: e.target.value })}
                      sx={{ minWidth: 150 }}
                    >
                      {ops.map((o) => (
                        <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                      ))}
                    </TextField>

                    {kind === 'between' ? (
                      <Stack
                        key={`${c.id}-between`}
                        direction="row" spacing={1} sx={{ flex: 1, minWidth: 180 }}
                      >
                        <TextField
                          size="small" label="From" value={c.value}
                          onChange={(e) => updateCondition(c.id, { value: e.target.value })}
                          fullWidth
                        />
                        <TextField
                          size="small" label="To" value={c.value2}
                          onChange={(e) => updateCondition(c.id, { value2: e.target.value })}
                          fullWidth
                        />
                      </Stack>
                    ) : kind === 'list' ? (
                      <Autocomplete
                        key={`${c.id}-list`}
                        multiple freeSolo size="small"
                        options={opts}
                        value={c.value ? c.value.split(',').map((s) => s.trim()).filter(Boolean) : []}
                        onChange={(_, vals) => updateCondition(c.id, { value: (vals as string[]).join(', ') })}
                        sx={{ flex: 1, minWidth: 200 }}
                        renderInput={(p) => (
                          <TextField {...p} label="Values" placeholder="type & press Enter" />
                        )}
                      />
                    ) : (
                      <Autocomplete
                        key={`${c.id}-single`}
                        freeSolo size="small"
                        options={opts}
                        inputValue={c.value}
                        onInputChange={(_, v) => updateCondition(c.id, { value: v })}
                        sx={{ flex: 1, minWidth: 160 }}
                        renderInput={(p) => (
                          <TextField {...p} label="Value" placeholder={isNum ? 'number' : 'text'} />
                        )}
                      />
                    )}

                    <Tooltip title="Remove condition">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => removeCondition(c.id)}
                          disabled={conditions.length <= 1}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                );
              })}
            </Stack>

            <Box>
              <Button size="small" startIcon={<AddIcon />} onClick={addCondition}>
                Add condition
              </Button>
            </Box>

            <Divider />

            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Expression preview:
              </Typography>
              <Chip
                size="small"
                variant="outlined"
                label={generated || '(no filter)'}
                sx={{ fontFamily: 'monospace', maxWidth: '100%' }}
              />
              {generated && (
                <Button size="small" startIcon={<CodeIcon />} onClick={editAsExpression}>
                  Edit as expression
                </Button>
              )}
            </Stack>
          </Stack>
        ) : (
          <Stack spacing={1.5}>
            <TextField
              multiline minRows={3} fullWidth autoFocus
              value={expression}
              onChange={(e) => setExpression(e.target.value)}
              placeholder="total_sales > 100000 and region in ('east','north')"
              InputProps={{ sx: { fontFamily: 'monospace', fontSize: 14 } }}
            />
            <Box sx={{ fontSize: 13, lineHeight: 1.6, color: 'text.secondary' }}>
              <strong>Syntax</strong> — refer to columns by name. Logical:{' '}
              <code>and</code>, <code>or</code>, <code>not</code>. Operators:{' '}
              <code>== != &lt; &lt;= &gt; &gt;=</code>, plus <code>in (…)</code>,{' '}
              <code>between … and …</code>, and <code>contains '…'</code> (text search).
              <Box sx={{ mt: 0.5 }}>
                <strong>Examples</strong><br />
                <code>total_sales &gt; 100000</code><br />
                <code>region == 'east' and order_count &gt;= 10</code><br />
                <code>region in ('north','south')</code><br />
                <code>customer_name contains 'corp'</code><br />
                <code>total_sales between 50000 and 150000</code>
              </Box>
            </Box>
          </Stack>
        )}

        {!validation.ok && (
          <Alert severity="error" sx={{ mt: 2 }}>{validation.error}</Alert>
        )}
        {validation.ok && validation.warning && (
          <Alert severity="warning" sx={{ mt: 2 }}>{validation.warning}</Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={clearAll} color="inherit" sx={{ mr: 'auto' }}>
          Clear all
        </Button>
        <Button onClick={onClose} color="inherit">Cancel</Button>
        <Button onClick={handleApply} variant="contained" disabled={!validation.ok || !!nameError}>
          {isEdit ? 'Save filter' : 'Add filter'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
